import { Amplify } from 'aws-amplify';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { CookieStorage } from 'aws-amplify/utils';
import defaultConfig from '@/config/defaultConfig';
import { environment, isProduction, isStage } from '@/utils/environment';

const { userPoolId, userPoolWebClientId, cognitoDomain } =
  defaultConfig.authorization.Auth;

const cookiePrefix = isProduction
  ? 'auth-prod'
  : isStage
    ? 'auth-stage'
    : 'auth-dev';

const getCookieDomain = (): string | undefined => {
  const hostname = window.location.hostname;

  // On bettermile.com domains, scope to the environment's parent domain so SSO
  // works across all products (back office, planner, etc.) within that environment
  if (hostname.endsWith('.bettermile.com') || hostname === 'bettermile.com') {
    switch (environment) {
      case 'prod':
        return '.bettermile.com';
      case 'stage':
        return '.stage.bettermile.com';
      default:
        return '.dev.bettermile.com';
    }
  }

  // Browsers reject cookies with domain=localhost (RFC 6265 registrable-domain
  // requirement). Omit the domain attribute so the browser stores a host-only
  // cookie instead, which works correctly on localhost.
  if (hostname === 'localhost') {
    return undefined;
  }

  // For external domains (e.g. CloudFront preview URLs), scope to exact hostname
  return hostname;
};

const cookieDomain = getCookieDomain();

const baseCookieStorage = new CookieStorage({
  ...(cookieDomain !== undefined && { domain: cookieDomain }),
  path: '/',
  expires: 365,
  sameSite: 'lax',
  secure: window.location.hostname !== 'localhost'
});

export const authStorage = {
  async setItem(key: string, value: string): Promise<void> {
    return baseCookieStorage.setItem(`${cookiePrefix}.${key}`, value);
  },

  async getItem(key: string): Promise<string | null> {
    return baseCookieStorage.getItem(`${cookiePrefix}.${key}`);
  },

  async removeItem(key: string): Promise<void> {
    return baseCookieStorage.removeItem(`${cookiePrefix}.${key}`);
  },

  async clear(): Promise<void> {
    const prefix = `${cookiePrefix}.`;
    const removals = document.cookie
      .split(';')
      .map((c) => (c.split('=')[0] ?? '').trim())
      .filter((name) => name.startsWith(prefix))
      .map((name) => baseCookieStorage.removeItem(name));
    await Promise.all(removals);
  }
};

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: userPoolId!,
      userPoolClientId: userPoolWebClientId!,
      loginWith: {
        oauth: {
          domain: cognitoDomain!,
          scopes: ['openid'],
          redirectSignIn: [window.location.origin],
          redirectSignOut: [window.location.origin],
          responseType: 'code'
        }
      }
    }
  }
});

// Must be called AFTER Amplify.configure(): configure() internally calls
// setKeyValueStorage(defaultStorage) as part of its setup, so this call
// must come last to ensure the custom cookie storage wins.
cognitoUserPoolsTokenProvider.setKeyValueStorage(authStorage);
